import React from 'react';
import PropTypes from 'prop-types';
import {
  BarChart as RBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  ResponsiveContainer,
} from 'recharts';
import useChartTheme from './useChartTheme';
import ChartTooltip from './ChartTooltip';
import styles from './Charts.module.scss';

/* ────────────────────────────────────────────────────────────
 * BarChart — themed Recharts wrapper.
 *
 *   <BarChart
 *     data={[{ day: 'Mon', orders: 24, refunds: 2 }, ...]}
 *     xKey="day"
 *     series={[
 *       { key: 'orders',  name: 'Orders' },
 *       { key: 'refunds', name: 'Refunds' },
 *     ]}
 *     height={320}
 *     valueFormatter={(v) => v.toLocaleString()}
 *   />
 *
 * Series colors auto-cycle through --chart-1 → --chart-8.
 * Override per series via `color: 'var(--chart-3)'` on the
 * series entry. A single-series chart needs no legend — pass
 * `showLegend={false}` and let the title name the measure.
 *
 * `layout="vertical"` draws horizontal bars (the right shape for
 * ranked categories with long names). Give it `yAxisWidth` so the
 * labels aren't clipped, and `showValueLabels` to print the value
 * at each bar's end — direct labels keep a low-contrast fill
 * readable without a hover.
 *
 * Animates in on mount per MOTION.md §4.10 (400ms ease-out-quart).
 * ──────────────────────────────────────────────────────────── */

/* Rounded data-end, anchored to the baseline: the corners that round
 * are the ones the bar grows toward. In a stack only the outermost
 * segment carries them, so the stack reads as one bar. Recharts takes
 * the radius as [topLeft, topRight, bottomRight, bottomLeft] in screen
 * space, whichever way the chart is laid out. */
const END_RADIUS = 4;
const barRadius = (isVertical, isLast) => {
  if (!isLast) return 0;
  return isVertical
    ? [0, END_RADIUS, END_RADIUS, 0]
    : [END_RADIUS, END_RADIUS, 0, 0];
};

const BarChart = ({
  data,
  xKey,
  series,
  height,
  stacked,
  layout,
  yAxisWidth,
  barSize,
  showValueLabels,
  valueFormatter,
  labelFormatter,
  showLegend,
  showGrid,
  showTooltip,
  className,
  ariaLabel,
}) => {
  const palette = useChartTheme();
  const isVertical = layout === 'vertical';

  return (
    <div
      className={[styles.chart, className || ''].filter(Boolean).join(' ')}
      style={{ height }}
      role="img"
      aria-label={ariaLabel}
    >
      <ResponsiveContainer width="100%" height="100%">
        <RBarChart
          data={data}
          layout={layout}
          /* Horizontal bars run to the right edge of the plot, so a
             value label printed past the bar end needs a gutter of
             its own or it is clipped. */
          margin={{
            top: 16,
            right: showValueLabels && isVertical ? 44 : 16,
            left: 0,
            bottom: 0,
          }}
        >
          {showGrid && (
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={palette.grid}
              vertical={layout === 'vertical'}
              horizontal={layout !== 'vertical'}
            />
          )}
          {layout === 'vertical' ? (
            <>
              <XAxis type="number" stroke={palette.axis} fontSize={12} tickLine={false} axisLine={false} />
              <YAxis
                type="category"
                dataKey={xKey}
                stroke={palette.axis}
                fontSize={12}
                tickLine={false}
                axisLine={false}
                width={yAxisWidth}
              />
            </>
          ) : (
            <>
              <XAxis dataKey={xKey} stroke={palette.axis} fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke={palette.axis} fontSize={12} tickLine={false} axisLine={false} />
            </>
          )}
          {showTooltip && (
            <Tooltip
              cursor={{ fill: palette.grid }}
              content={(p) => (
                <ChartTooltip
                  {...p}
                  valueFormatter={valueFormatter}
                  labelFormatter={labelFormatter}
                />
              )}
            />
          )}
          {showLegend && <Legend wrapperStyle={{ color: palette.axis, fontSize: 12 }} />}
          {series.map((s, i) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.name || s.key}
              fill={s.color || palette.series[i % palette.series.length]}
              radius={barRadius(isVertical, !stacked || i === series.length - 1)}
              stackId={stacked ? 'stack' : undefined}
              barSize={barSize}
              isAnimationActive
              animationDuration={400}
            >
              {showValueLabels && (!stacked || i === series.length - 1) && (
                <LabelList
                  dataKey={s.key}
                  position={isVertical ? 'right' : 'top'}
                  className={styles.valueLabel}
                  formatter={valueFormatter || undefined}
                />
              )}
            </Bar>
          ))}
        </RBarChart>
      </ResponsiveContainer>
    </div>
  );
};

BarChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  xKey: PropTypes.string.isRequired,
  series: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      name: PropTypes.string,
      color: PropTypes.string,
    })
  ).isRequired,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  stacked: PropTypes.bool,
  layout: PropTypes.oneOf(['horizontal', 'vertical']),
  // Gutter reserved for the category labels of a `vertical` layout.
  yAxisWidth: PropTypes.number,
  // Fixed bar thickness. Omitted, Recharts divides the band.
  barSize: PropTypes.number,
  // Print each bar's value at its end (MOTION/dataviz: selective
  // direct labels beat a hover-only readout on a low-contrast fill).
  showValueLabels: PropTypes.bool,
  valueFormatter: PropTypes.func,
  labelFormatter: PropTypes.func,
  showLegend: PropTypes.bool,
  showGrid: PropTypes.bool,
  showTooltip: PropTypes.bool,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

BarChart.defaultProps = {
  height: 320,
  stacked: false,
  layout: 'horizontal',
  yAxisWidth: undefined,
  barSize: undefined,
  showValueLabels: false,
  valueFormatter: null,
  labelFormatter: null,
  showLegend: true,
  showGrid: true,
  showTooltip: true,
  className: '',
  ariaLabel: 'Bar chart',
};

export default BarChart;
